import { Component, Inject, OnInit } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { select, Store } from '@ngrx/store'
import { Subject, throwError } from 'rxjs'
import { catchError, distinctUntilChanged, filter, finalize, map, takeUntil, tap, withLatestFrom } from 'rxjs/operators'
import * as lodash from 'lodash'

import * as fromRoot from '@tv3/store/state'
import { ChannelNameTokens, Destroyable, SaveForm, selectAllApiChannelViews, untilDestroy } from '@tokeet/tv3-platform'
import { Rental } from '@tokeet/tv3-platform'
import { Toaster } from '@tokeet/tv3-platform'
import { ConnectionService } from '@tv3/store/connection/connection.service'
import { RentalService, RateMap } from '@tokeet/tv3-platform'
import { RentalRateService } from '@tv3/services/rental-rate.service'
import { ConnectionView } from '@tv3/store/connection/connection.view'
import { ChannelRoomRateResponse, GetChannelRoomRatesPayload } from '@tv3/store/connection/connection.types'
import { rateMappingDisabledChannels } from '@tokeet-frontend/channels'
import { selectConnectionByLinkedRentalId } from '@tv3/store/connection/connection.selectors'
import { ConnectionTypes } from '@tv3/store/connection/connection.model'

export interface AddRateMappingDialogInput {
  rental: Rental
  rateMappings: RateMap[]
  connectionId?: string
}

export interface AddRateMappingDialogResult {
  rateMapping: RateMap
  rateMappings: RateMap[]
}
@Component({
  selector: 'app-add-rate-mapping-dialog',
  templateUrl: './add-rate-mapping-dialog.component.html',
  // tslint:disable use-host-property-decorator
  host: { class: 'modal-content' },
  styleUrls: ['./add-rate-mapping-dialog.component.scss'],
})
export class AddRateMappingDialogComponent extends Destroyable implements OnInit {
  form: FormGroup

  connections: ConnectionView[]
  categories: { id: string; name: string }[]

  roomRates: Record<string, ChannelRoomRateResponse[]> = {}
  loadingRoomRates: Record<string, boolean> = {}

  private unsubscribe$ = new Subject<void>()

  constructor(
    private formBuilder: FormBuilder,
    private store: Store<fromRoot.State>,
    private toaster: Toaster,
    private connectionService: ConnectionService,
    private rentalService: RentalService,
    private rentalRateService: RentalRateService,
    public dialogRef: MatDialogRef<AddRateMappingDialogComponent, AddRateMappingDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: AddRateMappingDialogInput
  ) {
    super()
    this.buildForm()
  }

  ngOnInit() {
    const { connectionId, rental } = this.data

    this.store
      .pipe(
        select(selectConnectionByLinkedRentalId(rental.id)),
        map((items) => items.filter((t) => t.type === ConnectionTypes.Api)),
        withLatestFrom(
          this.store
            .select(selectAllApiChannelViews)
            .pipe(map((channels) => channels.filter((c) => !rateMappingDisabledChannels.includes(c.name))))
        ),
        tap(([connections, channels]) => {
          this.connections = lodash.compact(
            connections.map((connection) => {
              const channel = channels.find((c) => {
                return connection.channelId.includes(c.id)
              })

              if (!channel) return null

              const connectionView = new ConnectionView(channel, rental, connection)

              return connectionView
            })
          )

          this.connections = lodash.sortBy(this.connections, (c) => c.channelFriendlyName.toLowerCase())

          if (connectionId) {
            const conn = lodash.find(this.connections, (c) => c.id === connectionId)
            if (conn) {
              this.form.patchValue({ connection: conn })
              this.form.get('connection').disable()
            }
          }
        }),
        untilDestroy(this)
      )
      .subscribe()

    this.rentalRateService
      .getRateCategories(this.data.rental.id)
      .pipe(
        tap((items) => {
          this.categories = items
        })
      )
      .subscribe()
  }

  get selectedChannelId() {
    const connection = this.form.get('connection').value as ConnectionView
    return (connection && connection.channelId) || null
  }

  private filterRoomRates(connection: ConnectionView, roomRates: ChannelRoomRateResponse[]) {
    if (!connection) return []

    const rateMappings = lodash.filter(this.data.rateMappings, (rateMapping: RateMap) => {
      return (
        lodash.toString(rateMapping.propertyId) === lodash.toString(connection.propertyId) &&
        lodash.toString(rateMapping.roomId) === lodash.toString(connection.roomId)
      )
    })

    const roomNames = {}

    rateMappings.forEach((rateMapping) => {
      roomNames[rateMapping.rateName] = true
    })

    roomRates = lodash.filter(roomRates, (roomRate: ChannelRoomRateResponse) => {
      return !roomNames[roomRate.name]
    })

    return roomRates
  }

  @SaveForm()
  onSubmit(form: FormGroup) {
    if (form.invalid) return

    const data = form.getRawValue()

    const category = data.category as string
    const connection = data.connection as ConnectionView
    const roomRate = data.roomRate as object

    const rateMap = new RateMap({
      category: category,
      rateId: roomRate['id'],
      maxGuest: roomRate['maxguest'],
      rateName: roomRate['name'],
      priceModel: roomRate['model'],
      roomId: connection.roomId as number,
      maxPersons: roomRate['max_per'],
      channelId: connection.channelId,
      channelName: connection.channel.friendlyName,
      api: connection.channel.name,
      propertyId: connection.propertyId as number,
    })

    form.disable()

    this.rentalService
      .addRateMapping(this.data.rental, rateMap)
      .pipe(
        tap((result) => {
          this.toaster.success('Rate mapping added successfully.')
          this.close(result)
        }),
        catchError((error) => {
          this.toaster.error('Unable to add rate mapping.')
          form.enable()

          return throwError(error)
        })
      )
      .subscribe()
  }

  close(result: AddRateMappingDialogResult = null) {
    this.dialogRef.close(result)
  }

  private buildForm() {
    const form = (this.form = this.formBuilder.group({
      category: [null, [Validators.required]],
      connection: [null, [Validators.required]],
      roomRate: [null, [Validators.required]],
    }))

    form
      .get('connection')
      .valueChanges.pipe(
        distinctUntilChanged(),
        tap((connection: ConnectionView) => {
          this.form.get('roomRate').setValue(null)
          this.fetchRoomRates(connection)
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe()
  }

  fetchRoomRates(connection: ConnectionView) {
    if (!connection) return

    const channel = connection.channel
    const channelId = connection.channelId

    if (this.loadingRoomRates[channelId]) return

    if (this.roomRates[channelId]) {
      this.checkEmptyRoomRates(connection, this.roomRates[channelId])
      return
    }

    this.loadingRoomRates[channelId] = true

    const payload: GetChannelRoomRatesPayload = {
      channel_id: channelId,
      api: channel.name,
      id: connection.roomId,
      propertyId: connection.propertyId,
    }

    this.connectionService
      .getRoomRatesFromConnection(channel.name, channelId, payload)
      .pipe(
        tap((roomRates: ChannelRoomRateResponse[]) => {
          roomRates = this.filterRoomRates(connection, roomRates)
          this.roomRates[channelId] = roomRates
          this.checkEmptyRoomRates(connection, roomRates)
        }),
        catchError((error) => {
          this.toaster.error(`Unable to load channel rate categories for ${connection.channelFriendlyName}.`)
          return throwError(error)
        }),
        finalize(() => {
          this.loadingRoomRates[channelId] = false
        })
      )
      .subscribe()
  }

  checkEmptyRoomRates(connection: ConnectionView, roomRates: ChannelRoomRateResponse[]) {
    if (!lodash.isEmpty(roomRates)) return
    this.toaster.warning(`No channel rate categories available for ${connection.channelFriendlyName}.`)
  }
}
